import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { BellRing, Trash, RefreshCcw } from 'lucide-react';

interface UpdateMessage {
  id: string;
  data: string;
  timestamp: string;
  type?: string;
}

const UpdateStream = () => {
  const [messages, setMessages] = useState<UpdateMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [topicFilter, setTopicFilter] = useState<string>('*');
  const eventSourceRef = useRef<EventSource | null>(null);
  const { toast } = useToast();

  const mercureUrl = "https://api.congness.local/.well-known/mercure";

  const connectToMercure = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Construct URL with topic parameter if needed
      const url = new URL(mercureUrl);
      if (topicFilter) {
        url.searchParams.append('topic', topicFilter);
      }

      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        toast({
          title: "Kapcsolódva",
          description: "Sikeresen kapcsolódtunk a frissítési adatfolyamhoz",
          variant: "default",
        });
      };

      eventSource.onmessage = (event) => {
        try {
          const data = event.data;
          const parsedData = JSON.parse(data);

          setMessages((prev) => [
            {
              id: crypto.randomUUID(),
              data: typeof parsedData === 'object' ? JSON.stringify(parsedData, null, 2) : data,
              timestamp: new Date().toISOString(),
              type: 'message'
            },
            ...prev
          ]);
        } catch (error) {
          console.error('Error parsing message:', error);
          setMessages((prev) => [
            {
              id: crypto.randomUUID(),
              data: event.data,
              timestamp: new Date().toISOString(),
              type: 'error'
            },
            ...prev
          ]);
        }
      };

      eventSource.onerror = (error) => {
        console.error('EventSource failed:', error);
        setIsConnected(false);
        toast({
          title: "Kapcsolat megszakadt",
          description: "A frissítési adatfolyamhoz való kapcsolat megszakadt",
          variant: "destructive",
        });

        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (eventSourceRef.current) {
            connectToMercure();
          }
        }, 5000);
      };

      return () => {
        eventSource.close();
        setIsConnected(false);
      };
    } catch (error) {
      console.error('Error connecting to Mercure:', error);
      toast({
        title: "Kapcsolódási hiba",
        description: "Nem sikerült kapcsolódni a frissítési adatfolyamhoz",
        variant: "destructive",
      });
      setIsConnected(false);
    }
  };

  useEffect(() => {
    connectToMercure();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [topicFilter]);

  const clearMessages = () => {
    setMessages([]);
    toast({
      title: "Üzenetek törölve",
      description: "Az összes üzenet sikeresen törölve",
    });
  };

  const reconnect = () => {
    connectToMercure();
    toast({
      title: "Újrakapcsolódás",
      description: "Újrakapcsolódás a frissítési adatfolyamhoz...",
    });
  };

  const formatTimestamp = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 pt-20 pb-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Élő Frissítések</h1>
          <p className="text-gray-600 mt-2">
            Itt láthatja az élő frissítéseket és értesítéseket.
          </p>
        </div>

        <div className="grid gap-6 grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BellRing className="h-5 w-5" />
                  Értesítések
                </CardTitle>
                <CardDescription>
                  <span className="text-green-600 flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-600"></span>
                    Aktív
                  </span>
                  <span className="text-red-600 flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full bg-red-600"></span>
                    Inaktív
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Button
                      variant="outline"
                      onClick={reconnect}
                      className="w-full flex items-center justify-center"
                    >
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Frissítés
                    </Button>
                  </div>

                  <div>
                    <Button
                      variant="destructive"
                      onClick={clearMessages}
                      className="w-full flex items-center justify-center"
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Törlés
                    </Button>
                  </div>

                  <div className="pt-4">
                    <h3 className="text-sm font-medium mb-2">Esemény statisztika</h3>
                    <p className="text-sm text-gray-500">
                      Összesen beérkezett: {messages.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-9">
            <Card className="h-[600px] flex flex-col">
              <CardHeader>
                <CardTitle>Élő üzenetfolyam</CardTitle>
                <CardDescription>
                  Az alábbiakban láthatja az élő üzeneteket és frissítéseket.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-1 p-0">
                <ScrollArea className="h-[500px] p-4">
                  {messages.length > 0 ? (
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div key={message.id} className="p-3 bg-white rounded-lg border">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-medium text-gray-500">
                              {formatTimestamp(message.timestamp)}
                            </span>
                            {message.type === 'error' && (
                              <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                                Hiba
                              </span>
                            )}
                          </div>

                          <Separator className="my-2" />

                          <pre className="text-xs overflow-auto p-2 bg-gray-50 rounded">
                            {message.data}
                          </pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">Még nincsenek üzenetek</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UpdateStream;
